import type { ImageAsset } from '../interfaces'
import type { InputAsset } from '../interfaces/inputAsset.interface'
import { nordyAxios } from '../api/client'

// const NoneOrientationMimeTypes = ['image/gif', 'image/webp']
export type InputItemType = File
export type Images = { [x: number]: InputItemType }
export enum AssetKind {
  Image = 'Image',
  Video = 'Video',
  Audio = 'Audio',
  Model3D = 'Model3D'
}

//! nordy - 파일 업로드 프론트엔드 검증
const ALLOWED_MIME_TYPES: Record<AssetKind, string[]> = {
  [AssetKind.Image]: [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/svg+xml'
  ],
  [AssetKind.Video]: ['video/mp4', 'video/webm', 'video/quicktime'],
  [AssetKind.Audio]: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm'],
  //! nordy - 3D 파일은 표준 MIME이 부재한 경우가 많아 브라우저가 빈 문자열
  // 또는 `application/octet-stream`을 보내는 경우가 흔하다. 추가로 알려진
  // 확장자별 MIME도 허용한다. 최종 확장자 검증은 백엔드 multerModel3dOptions가 담당.
  [AssetKind.Model3D]: [
    'application/octet-stream',
    'model/gltf-binary',
    'model/gltf+json',
    'model/stl',
    'model/obj',
    'model/vnd.usdz+zip',
    'application/json'
  ]
}

//! nordy - 3D 파일은 폴리곤 수에 따라 용량이 커서 더 큰 한도가 필요하다.
const MAX_FILE_SIZE_BY_KIND: Partial<Record<AssetKind, number>> = {
  [AssetKind.Model3D]: 500 * 1024 * 1024 // 500MB (nordy-api MODEL3D_MAX_BYTES와 동기화)
}
const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

const MODEL3D_EXTENSIONS = new Set([
  'glb',
  'gltf',
  'obj',
  'fbx',
  'stl',
  'usdz',
  // Gaussian Splat / Point Cloud (Load3D 입력 업로드용)
  'ply',
  'spz',
  'splat',
  'ksplat'
])

function validateFile(file: File, kind: AssetKind): void {
  const maxSize = MAX_FILE_SIZE_BY_KIND[kind] ?? DEFAULT_MAX_FILE_SIZE
  if (file.size > maxSize) {
    throw new Error(
      `File size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum ${maxSize / 1024 / 1024}MB`
    )
  }
  const allowed = ALLOWED_MIME_TYPES[kind]
  if (file.type && !allowed.includes(file.type)) {
    throw new Error(
      `File type "${file.type}" is not allowed for ${kind}. Allowed: ${allowed.join(', ')}`
    )
  }
  //! nordy - 3D 파일은 MIME이 비어있는 경우가 흔하므로 확장자도 한 번 더 검증.
  if (kind === AssetKind.Model3D) {
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ext || !MODEL3D_EXTENSIONS.has(ext)) {
      throw new Error(
        `Unsupported 3D file extension ".${ext}". Allowed: ${[...MODEL3D_EXTENSIONS].join(', ')}`
      )
    }
  }
}

export const createImageApi = async (file: InputItemType) => {
  validateFile(file, AssetKind.Image)
  const form = new FormData()
  const staticFile = file
  // 로드이미지할때 파일 변형으로 인해 주석처리
  // if (!NoneOrientationMimeTypes.includes(file.type)) {
  //   staticFile = await removeOrientationImage(file)
  // }
  form.append(
    'body',
    JSON.stringify({ name: file?.name, kind: AssetKind.Image })
  )
  form.append('image', staticFile)
  const { data: image_ } = await nordyAxios.post<ImageAsset>(
    '/v1/asset/file/image',
    form
  )
  return image_
}

export const createVideoApi = async (file: InputItemType) => {
  validateFile(file, AssetKind.Video)
  const form = new FormData()
  const staticFile = file
  // 로드이미지할때 파일 변형으로 인해 주석처리
  // if (!NoneOrientationMimeTypes.includes(file.type)) {
  //   staticFile = await removeOrientationImage(file)
  // }
  form.append('body', JSON.stringify({ name: file?.name }))
  form.append('video', staticFile)
  const { data: video_ } = await nordyAxios.post<ImageAsset>(
    '/v1/asset/file/video',
    form
  )
  return video_
}

//!nordy - 입력 에셋 업로드 타임아웃 상한 (5분). 행 방지용 안전망.
const INPUT_ASSET_UPLOAD_TIMEOUT_MS = 300_000

export const createInputAssetApi = async ({
  file,
  kind
}: {
  file: InputItemType
  kind: AssetKind
}) => {
  validateFile(file, kind)
  const form = new FormData()
  const staticFile = file
  if (kind === AssetKind.Video) {
    form.append('video', staticFile)
  } else if (kind === AssetKind.Audio) {
    form.append('audio', staticFile)
  } else if (kind === AssetKind.Model3D) {
    //! nordy - 백엔드 input-asset 컨트롤러가 'model3d' 필드명을 기대한다.
    form.append('model3d', staticFile)
  } else {
    form.append('image', staticFile)
  }
  form.append('body', JSON.stringify({ name: file?.name }))

  const apiUrlMap: Record<AssetKind, string> = {
    [AssetKind.Video]: '/v1/input-asset/file/video',
    [AssetKind.Audio]: '/v1/input-asset/file/audio',
    [AssetKind.Image]: '/v1/input-asset/file/image',
    [AssetKind.Model3D]: '/v1/input-asset/file/model3d'
  }
  const apiUrl = apiUrlMap[kind]

  //!nordy - 업로드는 무한 대기를 막기 위해 상한 타임아웃을 둔다.
  //   타임아웃이 없으면 느리거나 멈춘 업로드가 끝나지 않아, 업로드 상태가 영구히
  //   걸린 채로 남는다. 대용량 입력 에셋(3D 최대 500MB)을 고려해 여유 있게 둔다.
  const { data: inputAsset } = await nordyAxios.post<
    InputAsset<string, ImageAsset>
  >(apiUrl, form, { timeout: INPUT_ASSET_UPLOAD_TIMEOUT_MS })
  return inputAsset
}
